<script setup>
import HrExpansionItem from '../primitives/HrExpansionItem.vue';

/**
 * A named, collapsible group of menu rows — HrMenuItem's sibling for a menu that needs one level
 * of nesting.
 *
 * It exists because an HrExpansionItem dropped into a menu does not read as part of it. The row
 * is a list measure: body typography and a 56dp leading column sized for an avatar, against the
 * label-large type and 24dp icon gap every row beside it wears. The header ends up shorter and
 * set in a different face than the items it introduces, which is the one thing a group header
 * must not be.
 *
 * **One level.** A group holds HrMenuItems, never another group: a menu that nests twice is a
 * navigation tree, and the reader has lost the thing they opened the menu to do.
 *
 * **It runs nothing itself.** A group is a label and a disclosure; the actions are its children.
 * Give it no click handler — a header that both expands and acts fires one of the two by accident.
 *
 * @status recommended
 * @group actions
 * @useWhen A menu's entries fall into named sets large enough that a flat list would bury them
 * @avoidWhen The sets are small or few — a divider between them is lighter than a disclosure
 * @avoidWhen A set holds one entry — expanding a row to reveal its only child decides nothing
 * @related HrMenuItem
 * @related HrAdaptiveMenu
 * @a11y Inherits HrExpansionItem's disclosure semantics; the rows inside keep role="menuitem".
 */
defineOptions({ name: 'HrMenuGroup' });

const emit = defineEmits({
  /** Emitted when the group opens or closes; payload is the new open state. */
  'update:modelValue': null,
});

defineProps({
  /**
   * Group label text.
   * @example Rate
   */
  label: {
    type: String,
    default: null,
  },
  /** Material icon name shown before the label. */
  icon: {
    type: String,
    default: null,
  },
  /** Controlled open state; null means the group manages its own. */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /** Initial open state when uncontrolled. */
  defaultOpened: {
    type: Boolean,
    default: false,
  },
  /** Prevents opening and dims the header. */
  disable: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <hr-expansion-item
    class="hr-menu-group"
    :label="label"
    :icon="icon"
    :model-value="modelValue"
    :default-opened="defaultOpened"
    :disable="disable"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- @slot The group's rows. HrMenuItem only. -->
    <slot />
  </hr-expansion-item>
</template>

<style lang="scss">
/*
 * The same two corrections HrMenuItem makes, so the header and the rows under it are one list
 * rather than two. Kept in step with that file by hand; there is no shared mixin because the two
 * reach different elements.
 *
 * Matched on the root class rather than scoped, for the reason HrMenuItem documents: Vue rewrites
 * `[data-theme] :deep(x)` to `[data-theme][data-v-id] x`, which wants the theme attribute and the
 * scope id on one element, and data-theme sits on <html>.
 *
 * Not narrowed to the header row. The selector reaches the rows inside the group as well, and
 * those are HrMenuItems that set the same values — so the only cost of the wider match is that it
 * does not depend on Quasar's internal container class, which is the point.
 */
[data-theme] .hr-menu-group .q-item__label:not(.q-item__label--caption) {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

.hr-menu-group .q-item__section--avatar {
  min-width: auto;
  padding-inline-end: var(--app-comp-menu-item-leading-gap);
}

/*
 * Indented under their header, so an open group reads as one block and the next header as the
 * start of another. Flush, the rows of an open group and the headers around it were one column of
 * look-alike entries with nothing to say where the group ended.
 */
.hr-menu-group .q-expansion-item__content {
  padding-inline-start: var(--app-comp-menu-group-indent);
}

/*
 * A closed group's rows stay in the layout at no height rather than leaving it. QMenu places itself
 * once, against the width it has on opening, and never again when its content grows: a group whose
 * rows were longer than every header widened the menu past the edge it was aligned to and off the
 * screen. Measured with every row in it, the menu opens as wide as it will ever be.
 *
 * The !important overrides the inline display: none that QExpansionItem's v-show sets. The slide
 * transition still runs, because it animates an inline height, which wins over this one.
 */
.hr-menu-group.q-expansion-item--collapsed .q-expansion-item__content {
  display: block !important;
  height: 0;
  overflow: hidden;
  visibility: hidden;
}
</style>
